import { SpringConfig } from 'remotion';

// Types
import { AnimationState, AnimationPreset } from '@/types';

/**
 * Spring configurations for all animation presets
 * Includes both basic presets and advanced presets for animation components
 */
export const ANIMATION_SPRING_CONFIGS: Record<
  AnimationPreset,
  Partial<SpringConfig>
> = {
  /** Smooth, gentle animation */
  [AnimationPreset.GENTLE]: {
    damping: 200,
    mass: 0.5,
    stiffness: 100,
  },
  /** Standard animation (default) */
  [AnimationPreset.DEFAULT]: {
    damping: 200,
  },
  /** Quick, snappy animation */
  [AnimationPreset.SNAPPY]: {
    damping: 100,
    mass: 0.2,
    stiffness: 200,
  },
  /** Bouncy, playful animation */
  [AnimationPreset.BOUNCY]: { damping: 8, stiffness: 200, mass: 0.5 },
  /** Slot machine spin - very bouncy for dramatic effect */
  [AnimationPreset.SLOT_BOUNCE]: {
    damping: 6,
    stiffness: 180,
    mass: 0.4,
  },
  /** Wave text - smooth with slight overshoot */
  [AnimationPreset.WAVE_TEXT]: {
    damping: 12,
    stiffness: 280,
    mass: 0.3,
  },
  /** 3D flip - dramatic entrance */
  [AnimationPreset.FLIP_3D]: {
    damping: 14,
    stiffness: 120,
    mass: 0.6,
  },
  /** Elastic stretch effect */
  [AnimationPreset.ELASTIC]: {
    damping: 8,
    stiffness: 200,
    mass: 0.5,
  },
  /** Glow pulse - gentle for subtle effects */
  [AnimationPreset.GLOW]: {
    damping: 15,
    stiffness: 60,
    mass: 1,
  },
  /** Text slide - smooth for text entrances */
  [AnimationPreset.TEXT_SLIDE]: {
    damping: 18,
    stiffness: 150,
    mass: 0.4,
  },
  /** Container - general purpose smooth animation */
  [AnimationPreset.CONTAINER]: {
    damping: 20,
    stiffness: 100,
    mass: 0.5,
  },
} as const;

/**
 * Default timing configuration for animations
 */
export const ANIMATION_DEFAULTS = {
  startFrame: 0,
  stagger: 10,
} as const;

/**
 * Default animation state (hidden)
 */
export const DEFAULT_ANIMATION_STATE: AnimationState = {
  scale: 0,
  opacity: 0,
};

/**
 * Interpolation settings with clamping on both ends
 * Use this for interpolate() to clamp values at input range boundaries
 */
export const CLAMP_BOTH = {
  extrapolateLeft: 'clamp',
  extrapolateRight: 'clamp',
} as const;

export const CLAMP_RIGHT = {
  extrapolateRight: 'clamp',
} as const;

export const INTERPOLATE_0_1_CLAMP_BOTH = [[0, 1], [0, 1], CLAMP_BOTH] as const;

// Transition buffer to extend scene duration during transition
export const TRANSITION_BUFFER = 10;
